import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    Button, FormControl, Radio, RadioGroup, FormControlLabel, FormLabel,
    TextField, Card, CardContent, Box, Typography, Snackbar, SnackbarContent,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip, Badge
} from "@material-ui/core";
import ForumIcon from "@material-ui/icons/Forum";
import MessageIcon from "@material-ui/icons/Message";
import NoteService from "./Locality/Service/noteService";
import SchoolService from "./Locality/Service/schoolService";
import SocketService from "./Locality/Service/socketService";
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import SendIcon from '@material-ui/icons/Send';
import CloseIcon from '@material-ui/icons/Close';
import Widget from "../../components/Widget/Widget";

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: 200,
    },
    yellowHeader: {
        backgroundColor: '#FFC107',
        color: 'white',
        padding: '16px 24px',
        marginBottom: '24px',
        borderRadius: '4px 4px 0 0',
    },
    yellowButton: {
        backgroundColor: '#FFC107',
        color: 'white',
        padding: '16px',
        fontSize: '18px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        width: '100%',
        marginTop: '24px',
        '&:hover': {
            backgroundColor: '#FFB300',
        },
    },
    sectionTitle: {
        color: '#FFC107',
        fontWeight: 'bold',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    radioGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: '8px',
    },
    formCard: {
        maxWidth: '600px',
        margin: '0 auto',
        padding: '24px',
        marginBottom: '24px',
    },
    composeDialog: {
        '& .MuiDialog-paper': {
            maxWidth: 560,
            width: '100%',
        },
    },
    composeCard: {
        padding: 0,
        boxShadow: 'none',
    },
    notesListContainer: {
        maxWidth: '800px',
        margin: '0 auto',
    },
    /** Keeps title/actions visible; note cards scroll when there are many. */
    notesWidgetRoot: {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'min(720px, calc(100vh - 200px))',
        overflow: 'hidden',
        [theme.breakpoints.down('xs')]: {
            maxHeight: 'calc(100vh - 160px)',
        },
    },
    notesListHeader: {
        flexShrink: 0,
        padding: theme.spacing(2, 2, 1, 2),
    },
    notesListScroll: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: theme.spacing(0, 2, 2, 2),
    },
    noteBubble: {
        position: 'relative',
        padding: '14px 18px',
        borderRadius: 14,
        marginBottom: 12,
        backgroundColor: '#fffbe6',
        border: '1px solid #ffe38a',
        color: '#5b4407',
        cursor: 'pointer',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
        '&:hover': {
            backgroundColor: '#fff3bf',
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            transform: 'translateY(-1px)',
        },
    },
    noteBubbleUnread: {
        backgroundColor: '#fff5d6',
        borderColor: '#ffc107',
        boxShadow: '0 1px 4px rgba(255,193,7,0.25)',
    },
    noteHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
        flexWrap: 'wrap',
    },
    noteType: {
        fontWeight: 700,
        fontSize: '0.85rem',
        marginBottom: 6,
    },
    noteText: {
        fontSize: '0.9rem',
        marginBottom: 6,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    noteTime: {
        fontSize: '0.7rem',
        opacity: 0.75,
    },
    noteRowMeta: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginTop: 4,
    },
    unreadDot: {
        minWidth: 20,
        height: 20,
        padding: '0 6px',
        borderRadius: 10,
        backgroundColor: '#e53935',
        color: 'white',
        fontSize: '0.7rem',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    chatDialog: {
        '& .MuiDialog-paper': {
            width: '500px',
            maxWidth: '90vw',
            height: '600px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
        },
    },
    chatHeader: {
        backgroundColor: '#2196F3',
        color: 'white',
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    chatMessages: {
        flex: 1,
        overflowY: 'auto',
        padding: 16,
        backgroundColor: '#f5f5f5',
    },
    chatMessageBubble: {
        padding: '10px 16px',
        borderRadius: 18,
        marginBottom: 12,
        maxWidth: '70%',
        wordWrap: 'break-word',
    },
    messageSent: {
        backgroundColor: '#2196F3',
        color: 'white',
        marginLeft: 'auto',
        textAlign: 'right',
    },
    messageReceived: {
        backgroundColor: '#e0e0e0',
        color: '#000',
        marginRight: 'auto',
    },
    formNoteBubble: {
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        color: '#856404',
    },
    chatInput: {
        padding: 16,
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        gap: 8,
        alignItems: 'center',
    },
}));

export default function Note() {
    const classes = useStyles();
    const userDetails = useMemo(() => JSON.parse(localStorage.getItem("userDetail") || "{}"), []);
    const [schoolName, setSchoolName] = useState("");
    const [toast, setToast] = useState({
        open: false,
        type: 'success',
        message: '',
    });
    const [notesList, setNotesList] = useState([]);
    const [composeOpen, setComposeOpen] = useState(false);
    const [chatOpen, setChatOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const chatMessagesEndRef = useRef(null);
    const [unreadCounts, setUnreadCounts] = useState({}); // { noteId: count }
    const totalUnreadChats = useMemo(
        () => Object.values(unreadCounts).reduce((a, v) => a + (Number(v) || 0), 0),
        [unreadCounts],
    );
    const socketInitializedRef = useRef(false);
    const chatOpenRef = useRef(false);
    const selectedNoteRef = useRef(null);

    useEffect(() => {
        chatOpenRef.current = chatOpen;
    }, [chatOpen]);
    useEffect(() => {
        selectedNoteRef.current = selectedNote;
    }, [selectedNote]);

    useEffect(() => {
        if (userDetails && (userDetails.role === 'SCHOOLE' || userDetails.role === 'TEACHER' || userDetails.role === 'PARENT')) {
            const schooleId = userDetails.schooleId || userDetails.schoolId;
            if (schooleId) {
                SchoolService.getSchooleRegistrationById(schooleId)
                    .then((res) => {
                        if (res && res.schooleName) {
                            setSchoolName(res.schooleName);
                        }
                    })
                    .catch((err) => {
                        console.error('Error fetching school details:', err);
                    });
            }
        }
        loadNotes();

        // Initialize socket connection (also subscribes to unread events globally)
        if (!socketInitializedRef.current) {
            SocketService.initializeSocket();
            socketInitializedRef.current = true;
        }

        // Seed unread counts ONCE from a single aggregated endpoint (no per-note loop)
        NoteService.getUnreadSummary()
            .then((res) => {
                const data = res?.data || res || {};
                const byNote = data.byNoteId || {};
                setUnreadCounts(byNote);
            })
            .catch((err) => console.error('Error fetching unread summary:', err));

        // Real-time bump when someone sends a new chat to me
        const offUpdate = SocketService.onUnreadUpdate((payload) => {
            const noteId = payload?.noteId;
            if (!noteId) return;
            // If I'm currently looking at this exact thread, advance my server read
            // marker instead of incrementing the badge — keeps cross-device counts honest.
            if (
                chatOpenRef.current &&
                selectedNoteRef.current &&
                String(selectedNoteRef.current._id) === String(noteId)
            ) {
                if (!SocketService.markNoteReadSocket(noteId)) {
                    NoteService.markNoteRead(noteId).catch(() => {});
                }
                return;
            }
            setUnreadCounts((prev) => ({
                ...prev,
                [noteId]: (Number(prev[noteId]) || 0) + (Number(payload.increment) || 1),
            }));
        });

        // Real-time clear when *I* opened that thread (also fires on other devices)
        const offCleared = SocketService.onUnreadCleared((payload) => {
            const noteId = payload?.noteId;
            if (!noteId) return;
            setUnreadCounts((prev) => {
                if (!(noteId in prev)) return prev;
                const next = { ...prev };
                delete next[noteId];
                return next;
            });
        });

        return () => {
            if (typeof offUpdate === 'function') offUpdate();
            if (typeof offCleared === 'function') offCleared();
            // Don't disconnect the global socket here — Header (and other pages) share it.
            socketInitializedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [note, setNote] = useState({
        noteType: '',
        note: '',
    });

    const validationSchema = Yup.object().shape({
        noteType: Yup.string().required('Please select Note Type'),
        note: Yup.string(),
    });

    const formik = useFormik({
        initialValues: note,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values, { resetForm }) => {
            const submitData = {
                messageKind: 'NOTE',
                noteType: values.noteType,
                note: values.note || '',
            };

            NoteService.createNote(submitData).then((res) => {
                resetForm();
                setNote({
                    noteType: '',
                    note: '',
                });
                setComposeOpen(false);
                setToast({
                    open: true,
                    type: 'success',
                    message: 'Message sent successfully.',
                });
                loadNotes(); // Refresh notes list
            }).catch((err) => {
                setToast({
                    open: true,
                    type: 'error',
                    message: err.response?.data?.message || 'Error sending message',
                });
            });
        },
    });

    const loadNotes = async () => {
        try {
            const res = await NoteService.getAllNotes();
            const notes = res || [];
            setNotesList(notes);
        } catch (err) {
            console.error('Error fetching notes:', err);
        }
    };

    const loadChatMessages = async (noteId) => {
        try {
            if (!noteId) {
                console.error('No noteId provided to loadChatMessages');
                return;
            }
            console.log('Loading chat messages for noteId:', noteId);
            const res = await NoteService.getChatMessagesByNoteId(noteId);
            console.log('Chat messages response:', res);
            // httpService already extracts response.data, so res is the array directly
            const messages = Array.isArray(res) ? res : (res?.data || []);
            setChatMessages(messages);
            console.log('Set chat messages:', messages);
            
            // Scroll to bottom after messages load
            setTimeout(() => {
                if (chatMessagesEndRef.current) {
                    chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        } catch (err) {
            console.error('Error fetching chat messages:', err);
            setToast({
                open: true,
                type: 'error',
                message: err.response?.data?.message || 'Error loading chat messages',
            });
        }
    };

    const handleOpenChat = async (note) => {
        console.log('Opening chat for note:', note);
        if (!note?._id) {
            console.error('Note does not have _id:', note);
            setToast({
                open: true,
                type: 'error',
                message: 'Invalid note selected',
            });
            return;
        }
        setSelectedNote(note);
        setChatOpen(true);
        
        // Load initial chat messages
        await loadChatMessages(note._id);
        
        // Join socket room for real-time updates
        SocketService.joinNoteChat(note._id, {
            onJoined: (data) => {
                console.log('Joined room:', data);
            },
            onMessage: (messageData) => {
                console.log('Received real-time message:', messageData);
                // Add new message to chat
                setChatMessages(prev => {
                    // Check if message already exists (avoid duplicates)
                    const exists = prev.some(msg => msg._id === messageData._id);
                    if (exists) return prev;
                    return [...prev, messageData];
                });
                
                // Auto-scroll to bottom
                setTimeout(() => {
                    if (chatMessagesEndRef.current) {
                        chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100);
            },
            onError: (error) => {
                console.error('Socket error:', error);
                setToast({
                    open: true,
                    type: 'error',
                    message: error.message || 'Error connecting to chat',
                });
            },
        });
        
        // Optimistic clear; server will broadcast `unread-cleared` to other devices.
        setUnreadCounts(prev => {
            if (!(note._id in prev)) return prev;
            const updated = { ...prev };
            delete updated[note._id];
            return updated;
        });

        // Persist read-state server-side (socket if available, REST as fallback)
        try {
            const sentViaSocket = SocketService.markNoteReadSocket(note._id);
            if (!sentViaSocket) {
                await NoteService.markNoteRead(note._id);
            }
        } catch (err) {
            console.error('Error marking note as read:', err);
        }
    };

    const handleCloseChat = () => {
        // Leave socket room
        if (selectedNote?._id) {
            SocketService.leaveNoteChat(selectedNote._id);
        }
        setChatOpen(false);
        setSelectedNote(null);
        setChatMessages([]);
        setChatInput('');
    };

    const handleSendMessage = async () => {
        try {
            const text = (chatInput || '').trim();
            if (!text) return;
            if (!selectedNote?._id) return;

            const studentId = selectedNote?.studentId?._id || selectedNote?.studentId || userDetails?.studentId;
            if (!studentId) return;

            setChatInput('');
            
            // Try sending via socket first (real-time)
            if (SocketService.isConnected()) {
                SocketService.sendMessage(selectedNote._id, text, {
                    onSuccess: () => {
                        console.log('Message sent via socket');
                        // Message will be added via socket receive-message event
                        // No need to refresh notes list - socket handles it
                    },
                    onError: (error) => {
                        console.error('Socket send error, falling back to REST:', error);
                        // Fallback to REST API
                        sendViaRestAPI(studentId, text);
                    },
                });
            } else {
                // Fallback to REST API if socket not connected
                await sendViaRestAPI(studentId, text);
            }
        } catch (err) {
            console.error('Error sending message:', err);
            setToast({
                open: true,
                type: 'error',
                message: err.response?.data?.message || 'Error sending message',
            });
        }
    };

    const sendViaRestAPI = async (studentId, text) => {
        try {
            await NoteService.sendChatMessage({ 
                studentId, 
                note: text,
                parentNoteId: selectedNote._id
            });
            
            // Only reload messages and refresh notes list when using REST API
            await loadChatMessages(selectedNote._id);
            await loadNotes();
            
            // Scroll to bottom after sending
            setTimeout(() => {
                if (chatMessagesEndRef.current) {
                    chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        } catch (err) {
            console.error('Error sending via REST API:', err);
            setToast({
                open: true,
                type: 'error',
                message: err.response?.data?.message || 'Error sending message',
            });
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTimeShort = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const isMine = (msg) => {
        const me = userDetails?._id;
        const sender = msg?.createdBy?._id;
        return Boolean(me && sender && String(me) === String(sender));
    };

    return (
        <>
            <div className={classes.yellowHeader}>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={12}>
                    <Typography variant="h5" style={{ fontWeight: 'bold', color: 'white' }}>
                        {schoolName ? `  ${schoolName}` : ''}
                    </Typography>
                    <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={10}>
                    <Badge
                        badgeContent={totalUnreadChats > 0 ? totalUnreadChats : 0}
                        color="error"
                        overlap="rectangular"
                        invisible={totalUnreadChats <= 0}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                        <ForumIcon style={{ color: "white", fontSize: 30 }} aria-label="Unread chats" />
                    </Badge>
                    </Box>
                </Box>
            </div>

            {/* Notes List */}
            <Box className={classes.notesListContainer}>
                <Widget>
                    <Box className={classes.notesWidgetRoot}>
                        <Box className={classes.notesListHeader}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={12}>
                                <Typography variant="h6" style={{ fontWeight: 700 }}>
                                    Your Messages
                                </Typography>
                                <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={10}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<MessageIcon />}
                                        onClick={() => setComposeOpen(true)}
                                        style={{
                                            backgroundColor: '#0F3B62',
                                            color: '#fff',
                                            fontWeight: 700,
                                            textTransform: 'none',
                                        }}
                                    >
                                        Write a Message to school
                                    </Button>
                                    <Badge
                                        badgeContent={totalUnreadChats > 0 ? totalUnreadChats : 0}
                                        color="error"
                                        overlap="rectangular"
                                        invisible={totalUnreadChats <= 0}
                                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                                    >
                                        <ForumIcon style={{ color: "#0f3b62", fontSize: 28 }} aria-label="Unread chats" />
                                    </Badge>
                                </Box>
                            </Box>
                        </Box>
                        <Box className={classes.notesListScroll} role="region" aria-label="Your messages list">
                            {notesList.length === 0 ? (
                                <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    <Typography variant="body2">
                                        No messages yet. Use &quot;Write a note&quot; to send your first message to the school.
                                    </Typography>
                                </Box>
                            ) : (
                                notesList.map((note) => {
                                    const unreadCount = unreadCounts[note._id] || 0;
                                    return (
                                        <Box
                                            key={note._id}
                                            className={`${classes.noteBubble} ${unreadCount > 0 ? classes.noteBubbleUnread : ''}`}
                                            onClick={() => handleOpenChat(note)}
                                            style={{ width: '100%' }}
                                        >
                                            <Box className={classes.noteHeader}>
                                                {note.noteType && (
                                                    <Chip
                                                        label="Submitted"
                                                        size="small"
                                                        style={{
                                                            backgroundColor: '#4caf50',
                                                            color: 'white',
                                                            fontSize: '0.65rem',
                                                            height: '18px',
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            {note.noteType && (
                                                <Typography className={classes.noteType}>
                                                    📋 {note.noteType}
                                                </Typography>
                                            )}
                                            {note.note && (
                                                <Typography className={classes.noteText}>
                                                    {note.note}
                                                </Typography>
                                            )}
                                            <Box className={classes.noteRowMeta}>
                                                <Typography className={classes.noteTime}>
                                                    {formatTime(note.createdAt)}
                                                </Typography>
                                                {unreadCount > 0 && (
                                                    <span className={classes.unreadDot} aria-label={`${unreadCount} unread`}>
                                                        {unreadCount > 99 ? '99+' : unreadCount}
                                                    </span>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Box>
                </Widget>
            </Box>

            {/* New note (compose) dialog */}
            <Dialog
                open={composeOpen}
                onClose={() => {
                    formik.resetForm();
                    setComposeOpen(false);
                }}
                className={classes.composeDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle style={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Write a note
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            formik.resetForm();
                            setComposeOpen(false);
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Card className={classes.composeCard}>
                        <CardContent>
                            <form id="parent-note-compose-form" onSubmit={formik.handleSubmit}>
                                <FormControl component="fieldset" fullWidth style={{ marginBottom: '24px' }}>
                                    <FormLabel component="legend" className={classes.sectionTitle}>
                                        <span style={{ fontSize: '20px' }}>📋</span>
                                        Note Type*
                                    </FormLabel>
                                    <RadioGroup
                                        name="noteType"
                                        value={formik.values.noteType}
                                        onChange={formik.handleChange}
                                        className={classes.radioGroup}
                                    >
                                        <FormControlLabel
                                            value="Request for in person meeting with "
                                            control={<Radio color="primary" />}
                                            label="Request for in person meeting with "
                                        />
                                        <FormControlLabel
                                            value="Request for a call post school hours"
                                            control={<Radio color="primary" />}
                                            label="Request for a call post school hours"
                                        />
                                        <FormControlLabel
                                            value="Other"
                                            control={<Radio color="primary" />}
                                            label="Other"
                                        />
                                    </RadioGroup>
                                    {formik.touched.noteType && formik.errors.noteType && (
                                        <div style={{ color: '#d32f2f', fontSize: '0.8rem', marginTop: 6 }}>
                                            {formik.errors.noteType}
                                        </div>
                                    )}
                                </FormControl>

                                <Box style={{ marginBottom: '24px' }}>
                                    <FormLabel className={classes.sectionTitle} style={{ marginBottom: '8px' }}>
                                        Message (Optional)
                                    </FormLabel>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        name="note"
                                        value={formik.values.note}
                                        onChange={formik.handleChange}
                                        placeholder="Enter your message here..."
                                        variant="outlined"
                                        style={{ marginTop: '8px' }}
                                    />
                                </Box>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    className={classes.yellowButton}
                                    disabled={!formik.values.noteType || formik.isSubmitting}
                                >
                                    SEND
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            formik.resetForm();
                            setComposeOpen(false);
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Chat Dialog */}
            <Dialog
                open={chatOpen}
                onClose={handleCloseChat}
                className={classes.chatDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className={classes.chatHeader}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', color: 'white' }}>
                        Chat
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseChat}
                        style={{ color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.chatMessages}>
                    {chatMessages.length === 0 ? (
                        <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                            <Typography variant="body2">
                                No messages yet. Start a conversation!
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {chatMessages.map((m, idx) => {
                                // Check if it's a form note (has noteType and is not a CHAT message)
                                const isFormNote = m.noteType && (m.messageKind !== 'CHAT' || !m.messageKind);
                                return (
                                    <Box
                                        key={m._id || idx}
                                        className={`${classes.chatMessageBubble} ${
                                            isMine(m) ? classes.messageSent : classes.messageReceived
                                        }`}
                                        style={isFormNote ? { backgroundColor: '#fff3cd', border: '1px solid #ffc107', color: '#856404' } : {}}
                                    >
                                        {isFormNote && (
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                <Typography variant="caption" style={{ fontWeight: 700, fontSize: '0.7rem', opacity: 0.9 }}>
                                                    📋 {m.noteType}
                                                </Typography>
                                                <Chip
                                                    label="Submitted"
                                                    size="small"
                                                    style={{
                                                        backgroundColor: '#4caf50',
                                                        color: 'white',
                                                        fontSize: '0.65rem',
                                                        height: '18px',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        <Typography variant="body2">{m.note || '-'}</Typography>
                                        <Typography variant="caption" style={{ display: 'block', marginTop: 4, opacity: 0.75, fontSize: '0.7rem' }}>
                                            {formatTimeShort(m.createdAt)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                            <div ref={chatMessagesEndRef} />
                        </>
                    )}
                </DialogContent>
                <DialogActions className={classes.chatInput}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Type a message..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && chatInput.trim()) {
                                handleSendMessage();
                            }
                        }}
                        size="small"
                    />
                    <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!chatInput.trim()}
                    >
                        <SendIcon />
                    </IconButton>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <SnackbarContent
                    style={{
                        backgroundColor: toast.type === 'success' ? '#2e7d32' : '#d32f2f',
                        color: '#fff',
                        fontWeight: 600,
                    }}
                    message={toast.message}
                />
            </Snackbar>
        </>
    );
}
